import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Users, Building2, Bot, MessageSquare, Database, 
  Trash2, Shield, Search, Eye, Loader2, TrendingUp
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAgentBackendName = (agent) => agent?.name || agent?.display_name_effective || agent?.display_name || 'Agent';
const getAgentDisplayName = (agent) => agent?.display_name_effective || agent?.display_name || agent?.name || 'Agent';

const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [orgDetailDialog, setOrgDetailDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetail, setOrgDetail] = useState(null);
  const [orgDetailLoading, setOrgDetailLoading] = useState(false);
  
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, orgsRes, usersRes, agentsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/organizations`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/agents`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setOrganizations(orgsRes.data);
      setUsers(usersRes.data);
      setAgents(agentsRes.data);
    } catch (e) {
      console.error('Failed to fetch admin data:', e);
    } finally {
      setLoading(false);
    }
  };

  const viewOrgDetail = async (org) => {
    setSelectedOrg(org);
    setOrgDetailDialog(true);
    setOrgDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/organizations/${org.id}`, { withCredentials: true });
      setOrgDetail(data);
    } catch (e) {
      console.error('Failed to fetch org detail:', e);
    } finally {
      setOrgDetailLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteType === 'organization') {
        await axios.delete(`${API}/admin/organizations/${deleteTarget.id}`, { withCredentials: true });
        setOrganizations(organizations.filter(o => o.id !== deleteTarget.id));
      } else if (deleteType === 'user') {
        await axios.delete(`${API}/admin/users/${deleteTarget.id}`, { withCredentials: true });
        setUsers(users.filter(u => u.id !== deleteTarget.id));
      }
      // Refresh stats
      const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(data);
    } catch (e) {
      console.error('Delete failed:', e);
    } finally {
      setDeleting(false);
      setDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, { withCredentials: true });
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (e) {
      console.error('Failed to update role:', e);
    }
  };

  const statCards = stats ? [
    { label: 'Organizations', value: stats.total_organizations, icon: Building2, gradient: 'from-[#9333EA] to-[#A855F7]' },
    { label: 'Users', value: stats.total_users, icon: Users, gradient: 'from-[#EC4899] to-[#F472B6]' },
    { label: 'Agents', value: stats.total_agents, icon: Bot, gradient: 'from-[#9333EA] to-[#EC4899]' },
    { label: 'Conversations', value: stats.total_conversations, icon: MessageSquare, gradient: 'from-[#22C55E] to-[#4ADE80]' },
    { label: 'Messages', value: stats.total_messages, icon: TrendingUp, gradient: 'from-[#F59E0B] to-[#FBBF24]' },
    { label: 'Knowledge Entries', value: stats.total_knowledge_entries, icon: Database, gradient: 'from-[#6366F1] to-[#818CF8]' },
  ] : [];

  const filteredOrgs = organizations.filter(o => 
    o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.owner_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAgents = agents.filter(a =>
    getAgentBackendName(a).toLowerCase().includes(searchTerm.toLowerCase()) ||
    getAgentDisplayName(a).toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.organization_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleConnectGoogleCalendar = async () => {
    try {
      const res = await startGoogleCalendarConnection({ name: googleAccountLabel });
      const url = res.data?.authorization_url;
      if (!url) throw new Error('No Google authorization URL returned');
      window.location.href = url;
    } catch (error) {
      console.error(error);
    }
  };

  const getCategoryClass = (category) => {
    const classes = {
      customer_service: 'badge-customer-service',
      appointment_setting: 'badge-appointment-setting',
      sales: 'badge-sales',
      routing: 'badge-routing'
    };
    return classes[category] || '';
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#9333EA]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8" data-testid="admin-page">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight font-['Outfit']">Admin Panel</h1>
          <p className="text-muted-foreground mt-1">Manage tenants and monitor platform usage</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {statCards.map((stat, i) => (
            <div 
              key={stat.label}
              className="apollo-card p-4 animate-fade-in"
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.gradient} flex items-center justify-center mb-3`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-2xl font-semibold font-['Outfit']">{stat.value}</p>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Activity Summary */}
        {stats && (
          <div className="apollo-card p-4 mb-8">
            <h3 className="font-semibold mb-2">Last 7 Days</h3>
            <div className="flex gap-6 text-sm">
              <span><strong className="text-[#9333EA]">{stats.users_last_7_days}</strong> new users</span>
              <span><strong className="text-[#EC4899]">{stats.conversations_last_7_days}</strong> conversations</span>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search organizations, users, or agents..."
            className="pl-10 rounded-lg"
            data-testid="admin-search"
          />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="organizations">
          <TabsList className="mb-4">
            <TabsTrigger value="organizations" className="gap-2">
              <Building2 className="w-4 h-4" />
              Organizations ({filteredOrgs.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="w-4 h-4" />
              Users ({filteredUsers.length})
            </TabsTrigger>
            <TabsTrigger value="agents" className="gap-2">
              <Bot className="w-4 h-4" />
              Agents ({filteredAgents.length})
            </TabsTrigger>
          </TabsList>

          {/* Organizations Tab */}
          <TabsContent value="organizations">
            <div className="apollo-card">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Organization</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Owner</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">Members</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">Agents</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">Conversations</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Created</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrgs.map((org) => (
                      <tr key={org.id} className="border-b border-border hover:bg-secondary/50" data-testid={`org-row-${org.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#9333EA]/20 to-[#EC4899]/20 flex items-center justify-center">
                              <Building2 className="w-4 h-4 text-[#9333EA]" />
                            </div>
                            <span className="font-medium">{org.name}</span>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{org.owner_email}</td>
                        <td className="p-4 text-center">{org.member_count}</td>
                        <td className="p-4 text-center">{org.agent_count}</td>
                        <td className="p-4 text-center">{org.conversation_count}</td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {new Date(org.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewOrgDetail(org)}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setDeleteTarget(org);
                                setDeleteType('organization');
                                setDeleteDialog(true);
                              }}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="apollo-card">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">User</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Email</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Organization</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Role</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Created</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id} className="border-b border-border hover:bg-secondary/50" data-testid={`user-row-${u.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9333EA] to-[#EC4899] flex items-center justify-center text-white text-sm font-medium">
                              {u.name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <span className="font-medium">{u.name}</span>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{u.email}</td>
                        <td className="p-4 text-sm">{u.organization_name || <span className="text-muted-foreground">—</span>}</td>
                        <td className="p-4">
                          <Select value={u.role} onValueChange={(v) => updateUserRole(u.id, v)}>
                            <SelectTrigger className="w-24 h-8 rounded-lg">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeleteTarget(u);
                              setDeleteType('user');
                              setDeleteDialog(true);
                            }}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Agents Tab */}
          <TabsContent value="agents">
            <div className="apollo-card">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Agent</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Category</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Organization</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">LLM</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">Conversations</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAgents.map((a) => (
                      <tr key={a.id} className="border-b border-border hover:bg-secondary/50" data-testid={`agent-row-${a.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#9333EA]/20 to-[#EC4899]/20 flex items-center justify-center">
                              <Bot className="w-4 h-4 text-[#9333EA]" />
                            </div>
                            <div>
                              <span className="font-medium">{getAgentBackendName(a)}</span>
                              {getAgentDisplayName(a) !== getAgentBackendName(a) && (
                                <p className="text-xs text-muted-foreground">Widget name: {getAgentDisplayName(a)}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getCategoryClass(a.category)}`}>
                            {a.category?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="p-4 text-sm">{a.organization_name}</td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {a.llm_provider} / {a.llm_model?.split('-').slice(0, 2).join('-')}
                        </td>
                        <td className="p-4 text-center">{a.conversation_count}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <span className={`status-dot ${a.is_active ? 'active' : 'inactive'}`}></span>
                            <span className="text-sm">{a.is_active ? 'Active' : 'Inactive'}</span>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {new Date(a.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Organization Detail Dialog */}
        <Dialog open={orgDetailDialog} onOpenChange={setOrgDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedOrg?.name}</DialogTitle>
            </DialogHeader>
            {orgDetailLoading ? (
              <div className="py-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#9333EA]" />
              </div>
            ) : orgDetail && (
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <p className="text-2xl font-semibold">{orgDetail.stats.conversations}</p>
                    <p className="text-sm text-muted-foreground">Conversations</p>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <p className="text-2xl font-semibold">{orgDetail.stats.messages}</p>
                    <p className="text-sm text-muted-foreground">Messages</p>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <p className="text-2xl font-semibold">{orgDetail.stats.knowledge_entries}</p>
                    <p className="text-sm text-muted-foreground">KB Entries</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Owner</h4>
                  <p className="text-sm text-muted-foreground">{orgDetail.owner_name} ({orgDetail.owner_email})</p>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Members ({orgDetail.members.length})</h4>
                  <div className="space-y-2">
                    {orgDetail.members.map((m) => (
                      <div key={m.id} className="flex items-center justify-between p-2 bg-secondary rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#9333EA] to-[#EC4899] flex items-center justify-center text-white text-xs">
                            {m.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <span className="text-sm">{m.name}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{m.email}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Agents ({orgDetail.agents.length})</h4>
                  <div className="space-y-2">
                    {orgDetail.agents.map((a) => (
                      <div key={a.id} className="flex items-center justify-between p-2 bg-secondary rounded-lg">
                        <div className="flex items-center gap-2">
                          <Bot className="w-4 h-4 text-[#9333EA]" />
                          <div>
                            <span className="text-sm">{getAgentBackendName(a)}</span>
                            {getAgentDisplayName(a) !== getAgentBackendName(a) && (
                              <p className="text-xs text-muted-foreground">Widget name: {getAgentDisplayName(a)}</p>
                            )}
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getCategoryClass(a.category)}`}>
                            {a.category?.replace('_', ' ')}
                          </span>
                        </div>
                        <span className={`status-dot ${a.is_active ? 'active' : 'inactive'}`}></span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Delete {deleteType === 'organization' ? 'Organization' : 'User'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteType === 'organization' 
                  ? `Are you sure you want to delete "${deleteTarget?.name}"? This will permanently delete all agents, conversations, and data associated with this organization.`
                  : `Are you sure you want to delete user "${deleteTarget?.email}"?`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-500 hover:bg-red-600 rounded-lg"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default AdminPage;
